import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Flag, CheckCircle, Clock, Filter,
  AlertCircle, X, ChevronRight, FileText,
  User, Calendar, Shield, Gavel, Loader2, RefreshCw, ArrowLeft, Copy
} from 'lucide-react';
import { useUserReports } from '../../authentication_components/userReportContext';

// =================================================================
// 1. ReportResolutionDialog Component
// =================================================================
const ReportResolutionDialog = ({ isOpen, onClose, report, onResolve }) => {
  const [verdict, setVerdict] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setVerdict('');
  }, [isOpen]);

  if (!isOpen || !report) return null;

  const handleConfirm = async () => {
    if (!verdict.trim()) return;

    setIsSubmitting(true);
    try {
      await onResolve(report.report_id, verdict);
      onClose();
    } catch (error) {
      console.error("Resolution failed", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Gavel className="h-5 w-5 text-violet-600" />
            <span>Resolver Reporte</span>
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-200 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6 bg-amber-50 border border-amber-100 rounded-lg p-4">
            <div className="flex gap-2 mb-2">
              <Flag className="h-4 w-4 text-amber-600 mt-0.5" />
              <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Razón del Reporte</span>
            </div>
            <p className="text-sm text-slate-700 font-medium">{report.report_reason}</p>
          </div>

          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Veredicto / Acción Tomada
          </label>
          <textarea
            rows={4}
            className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none text-sm text-slate-700 resize-none"
            placeholder="Escriba los detalles de la resolución aquí (ej. Se contactó al usuario, se eliminó el contenido, se emitió advertencia, etc.)"
            value={verdict}
            onChange={(e) => setVerdict(e.target.value)}
          />

          <div className="mt-6 flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={!verdict.trim() || isSubmitting}
              className="flex-1 px-4 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 shadow-md shadow-violet-200 disabled:opacity-50 disabled:shadow-none font-medium transition-all flex items-center justify-center"
            >
              {isSubmitting ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : 'Confirmar Resolución'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 2. ReportDetailsDrawer (Side Panel)
// =================================================================
const ReportDetailsDrawer = ({ report, onClose, onOpenResolution, formatData }) => {
  if (!report) return null;

  const displayData = formatData ? formatData(report) : report;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div
        className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm transition-opacity opacity-100 animate-in fade-in duration-200"
        onClick={onClose}
      />

      <div
        className="relative w-full sm:w-96 bg-white shadow-2xl border-l border-slate-200 h-full transform transition-transform duration-300 ease-in-out animate-in slide-in-from-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Detalles del Reporte</h2>
              <p className="text-xs text-slate-500 font-mono">{report.report_id}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-white rounded-full transition-all">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="p-1.5 -ml-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors sm:hidden"
                aria-label="Volver"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>

              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${report.report_resolved
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                  : 'bg-amber-50 text-amber-700 border-amber-100'
                }`}>
                {report.report_resolved ? <CheckCircle className="h-4 w-4 mr-2" /> : <Clock className="h-4 w-4 mr-2" />}
                {report.report_resolved ? 'Resuelto' : 'Pendiente de Revisión'}
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="h-4 w-4 text-violet-500" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Negocio Reportado</span>
                </div>
                <h3 className="font-bold text-slate-800 text-md truncate" title={report.profile_id}>
                  Profile ID: {report.profile_id}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Este reporte está asociado al perfil de negocio público.
                </p>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <User className="h-4 w-4 text-violet-500" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Reportado Por</span>
                </div>
                <p className="text-sm text-slate-600 font-mono break-all">{report.user_id}</p>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                  <span className="flex items-center"><Calendar className="h-3 w-3 mr-1" /> {report.report_date}</span>
                  <span className="flex items-center"><Clock className="h-3 w-3 mr-1" /> {report.report_time}</span>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Flag className="h-4 w-4 text-red-500" />
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Motivo</span>
                </div>
                <p className="text-slate-800 font-medium text-sm leading-relaxed">"{report.report_reason}"</p>
              </div>

              {report.report_resolved && report.report_verdict && (
                <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Gavel className="h-4 w-4 text-emerald-600" />
                    <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Veredicto</span>
                  </div>
                  <p className="text-emerald-900 text-sm leading-relaxed">{report.report_verdict}</p>
                  {report.report_resolved_timestamp && (
                    <p className="text-emerald-600 text-[10px] mt-3 text-right">
                      Resuelto: {new Date(report.report_resolved_timestamp).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-slate-200 bg-slate-50">
            {!report.report_resolved ? (
              <button
                onClick={() => onOpenResolution(report)}
                className="w-full py-3 bg-violet-600 text-white rounded-xl hover:bg-violet-700 shadow-lg shadow-violet-200 font-semibold transition-all flex items-center justify-center gap-2"
              >
                <Gavel className="h-5 w-5" /> Resolver Reporte
              </button>
            ) : (
              <button disabled className="w-full py-3 bg-slate-200 text-slate-500 rounded-xl font-medium flex items-center justify-center gap-2 cursor-not-allowed">
                <CheckCircle className="h-5 w-5" /> Caso Cerrado
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 3. MAIN COMPONENT: BusinessReports
// =================================================================
export default function BusinessReports() {
  const {
    getReports,
    resolveReport,
    loading: contextLoading,
    error,
    formatReportData,
    clearError
  } = useUserReports();

  const [reports, setReports] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const [reportToResolve, setReportToResolve] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchAllReports = useCallback(async () => {
    try {
      const result = await getReports({ limit: 100 });
      setReports(result.reports || []);
    } catch (err) {
      console.error("Error fetching reports:", err);
    } finally {
      setIsInitialLoad(false);
    }
  }, [getReports]);

  useEffect(() => {
    fetchAllReports();
  }, [fetchAllReports]);

  const filteredReports = reports.filter(report => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (report.report_id && report.report_id.toLowerCase().includes(searchLower)) ||
      (report.profile_id && report.profile_id.toLowerCase().includes(searchLower)) ||
      (report.report_reason && report.report_reason.toLowerCase().includes(searchLower));

    const matchesStatus =
      filterStatus === 'all' ? true :
        filterStatus === 'resolved' ? report.report_resolved :
          !report.report_resolved;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: reports.length,
    pending: reports.filter(r => !r.report_resolved).length,
    resolved: reports.filter(r => r.report_resolved).length
  };

  const openResolutionDialog = (report) => {
    setReportToResolve(report);
    setIsResolutionOpen(true);
  };

  const handleResolution = async (reportId, verdict) => {
    await resolveReport(reportId, verdict);
    const timestamp = new Date().toISOString();

    setReports(prev => prev.map(r =>
      r.report_id === reportId
        ? { ...r, report_resolved: true, report_verdict: verdict, report_resolved_timestamp: timestamp }
        : r
    ));

    if (selectedReport && selectedReport.report_id === reportId) {
      setSelectedReport(prev => ({
        ...prev,
        report_resolved: true,
        report_verdict: verdict,
        report_resolved_timestamp: timestamp
      }));
    }
  };

  const refreshData = () => {
    setSearchTerm('');
    fetchAllReports();
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  if (isInitialLoad) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-violet-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Cargando reportes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 pb-12 relative">
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Reportes de Negocios</h1>
              <p className="text-sm text-slate-500">Gestiona y resuelve las denuncias enviadas por los usuarios.</p>
            </div>
            <div className="flex gap-3">
              <div className="bg-amber-50 px-4 py-2 rounded-lg border border-amber-100 flex flex-col items-center min-w-[100px]">
                <span className="text-xs font-bold text-amber-700 uppercase">Pendientes</span>
                <span className="text-xl font-bold text-amber-900">{stats.pending}</span>
              </div>
              <div className="bg-emerald-50 px-4 py-2 rounded-lg border border-emerald-100 flex flex-col items-center min-w-[100px]">
                <span className="text-xs font-bold text-emerald-700 uppercase">Resueltos</span>
                <span className="text-xl font-bold text-emerald-900">{stats.resolved}</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
              <button onClick={clearError} className="text-red-500 hover:text-red-700">
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por Report ID, Profile ID o Razón..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2 items-center">
              <div className="flex bg-slate-100 p-1 rounded-lg">
                <button
                  onClick={() => setFilterStatus('pending')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterStatus === 'pending' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Pendientes
                </button>
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterStatus === 'all' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Todos
                </button>
                <button
                  onClick={() => setFilterStatus('resolved')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterStatus === 'resolved' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Resueltos
                </button>
              </div>
              <button
                onClick={refreshData}
                title="Actualizar datos"
                className="p-2.5 bg-white border border-slate-200 text-slate-500 rounded-lg hover:bg-slate-50 hover:text-violet-600 transition-colors"
              >
                <RefreshCw className={`h-5 w-5 ${contextLoading && !isInitialLoad ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden min-h-[400px]">
          {filteredReports.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-96 text-center px-4">
              <div className="bg-slate-50 p-4 rounded-full mb-3">
                <FileText className="h-8 w-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800">No hay reportes que coincidan</h3>
              <p className="text-slate-500 max-w-xs mx-auto mt-1">
                {searchTerm ? 'Intenta buscar con otro término.' : 'No se encontraron reportes en esta categoría.'}
              </p>
              <button onClick={refreshData} className="mt-4 text-violet-600 font-medium text-sm hover:underline">
                Recargar datos
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-32">Report ID</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-40">Profile ID</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-32">Fecha</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Razón</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-32">Estado</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-16 text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredReports.map((report) => (
                    <tr
                      key={report.report_id}
                      onClick={() => setSelectedReport(report)}
                      className={`group cursor-pointer transition-colors ${selectedReport?.report_id === report.report_id ? 'bg-violet-50' : 'hover:bg-slate-50'}`}
                    >
                      <td className="px-6 py-4">
                        <span className="font-mono text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded">
                          {report.report_id.substring(0, 8)}...
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="text-xs font-medium text-slate-900 font-mono">{report.profile_id}</div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(report.profile_id, report.report_id);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-200 rounded"
                            title="Copiar Profile ID"
                          >
                            <Copy className={`h-3.5 w-3.5 ${copiedId === report.report_id ? 'text-emerald-600' : 'text-slate-400'}`} />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          {report.report_date}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-700 truncate max-w-[250px]" title={report.report_reason}>
                          {report.report_reason}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        {report.report_resolved ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                            <CheckCircle className="h-3 w-3 mr-1" /> Resuelto
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100">
                            <AlertCircle className="h-3 w-3 mr-1" /> Pendiente
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <ChevronRight className={`h-5 w-5 text-slate-300 ml-auto transition-transform ${selectedReport?.report_id === report.report_id ? 'rotate-90 text-violet-500' : ''}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ReportDetailsDrawer
        report={selectedReport}
        onClose={() => setSelectedReport(null)}
        onOpenResolution={openResolutionDialog}
        formatData={formatReportData}
      />

      <ReportResolutionDialog
        isOpen={isResolutionOpen}
        onClose={() => setIsResolutionOpen(false)}
        report={reportToResolve}
        onResolve={handleResolution}
      />
    </div>
  );
}